import { Typography, Box } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { useDateTime } from "../../../../../../localization/useDateTime"
import { MeetingDetailsOrder } from "../../../../../../nonAdminUser/reports/home/meetingServiceApi"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { TranslationKey } from "../../../../../../localization/translations"
import { Stack } from "@mui/system"
import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"

const useStyles = makeStyles()((theme) => ({
    sectionTitle: {
        fontWeight: 600,
        lineHeight: "1.5rem",
        color: theme.palette.tailwind.slate[700],
        marginBottom: theme.spacing(2),
        [theme.breakpoints.down("sm")]: {
            fontWeight: 500,
            color: theme.palette.tailwind.slate[800],
        },
    },
    detailsContainer: {
        gap: "0.5rem",
        "& > div": {
            [theme.breakpoints.down("sm")]: {
                marginBottom: theme.spacing(2),
            },
        },
    },
    detailRow: {
        display: "flex",
        justifyContent: "space-between",
    },
    detailLabel: {
        color: theme.palette.tailwind.slate[600],
        lineHeight: 1,
    },
    detailValue: {
        fontWeight: 500,
        textAlign: "right",
        color: theme.palette.tailwind.slate[900],
        lineHeight: 1,
    },
    deliveryDetailsContainer: {
        margin: theme.spacing(4, 0),
        [theme.breakpoints.down("sm")]: {
            margin: theme.spacing(3, 0),
        },
    },
}))

interface DeliveryDetailsRowProps {
    labelId: TranslationKey
    label: string
    value: string | number
}

const DeliveryDetailsRow = ({ labelId, label, value }: DeliveryDetailsRowProps) => {
    const { classes } = useStyles()

    return (
        <Box className={classes.detailRow}>
            <LocalizedStrict id={labelId}>
                <Typography variant="body1" className={classes.detailLabel}>
                    {label}
                </Typography>
            </LocalizedStrict>
            <Typography variant="body1" className={classes.detailValue}>
                {value}
            </Typography>
        </Box>
    )
}

interface DeliveryDetailsProps {
    order: MeetingDetailsOrder
}

export default function DeliveryDetails({ order }: DeliveryDetailsProps) {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()
    const { l10n } = useAppLocalization()

    const deliveryTypeValue = l10n.getStringForEnum("delivery-type-filter",  order.deliveryDetails.type,  order.deliveryDetails.type)

    return (
        <Box className={classes.deliveryDetailsContainer}>
            <LocalizedStrict id="production-package-slip-section-delivery-details-title">
                <Typography variant="h6" className={classes.sectionTitle}>
                    Delivery details
                </Typography>
            </LocalizedStrict>

            <Stack className={classes.detailsContainer}>
                <DeliveryDetailsRow
                    labelId="production-package-slip-section-delivery-details-type"
                    label="Delivery type:"
                    value={deliveryTypeValue}
                />
                <DeliveryDetailsRow
                    labelId="production-package-slip-section-delivery-details-time"
                    label="Delivery time:"
                    value={
                        order.deliveryDetails.time
                            ? dateTimeFactory.toHourMinute(dateTimeFactory.fromApi(order.deliveryDetails.time))
                            : "-"
                    }
                />
                <DeliveryDetailsRow
                    labelId="production-package-slip-section-delivery-details-meeting-time"
                    label="Meeting time:"
                    value={
                        order.meetingDetails?.startDateTime && order.meetingDetails?.endDateTime
                            ? dateTimeFactory.formatPeriod(
                                  dateTimeFactory.fromApi(order.meetingDetails.startDateTime),
                                  dateTimeFactory.fromApi(order.meetingDetails.endDateTime),
                              )
                            : "-"
                    }
                />
                <DeliveryDetailsRow
                    labelId="production-package-slip-section-delivery-details-location"
                    label="Delivery location:"
                    value={order.deliveryDetails.location.name || "-"}
                />

                <DeliveryDetailsRow
                    labelId="production-package-slip-section-delivery-details-sittings"
                    label="Sittings:"
                    value={order.deliveryDetails.sittings || "-"}
                />
                <DeliveryDetailsRow
                    labelId="production-package-slip-section-delivery-details-merchant"
                    label="Merchant:"
                    value={order.webshop?.merchant?.name || "-"}
                />
            </Stack>
        </Box>
    )
}
